// ══════════════════════════════════════════════════════════════════════════════
// PriestDashboardPage.js — Priest's personal dashboard
// Access: /?priest_dashboard=<priest_id>&secret=<admin_secret>
// ══════════════════════════════════════════════════════════════════════════════
import { useState, useEffect } from "react";
import PujaRoom from "./PujaRoom";

const API = (process.env.REACT_APP_API_URL || "http://localhost:8000").replace(/\/$/, "");

const C = {
  bg:     "#06030A", bg2: "#0D0818", bg3: "#130D22",
  saffron:"#E8832A", gold: "#C9953A", cream: "#FFF5E0",
  text:   "#EDE0C8", dim:  "#8A7558",
  border: "rgba(201,149,58,0.25)",
  glow:   "rgba(232,131,42,0.12)",
};

const STATUS_COLORS = {
  pending:   { bg: "rgba(250,204,21,0.1)",  border: "rgba(250,204,21,0.35)",  text: "#fde68a" },
  confirmed: { bg: "rgba(52,211,153,0.1)",  border: "rgba(52,211,153,0.35)",  text: "#6ee7b7" },
  completed: { bg: "rgba(139,92,246,0.1)",  border: "rgba(139,92,246,0.35)",  text: "#c4b5fd" },
  cancelled: { bg: "rgba(239,68,68,0.08)",  border: "rgba(239,68,68,0.3)",    text: "#fca5a5" },
};

function StatusBadge({ status }) {
  const s = STATUS_COLORS[status] || STATUS_COLORS.pending;
  return (
    <span style={{
      background: s.bg, border: `1px solid ${s.border}`,
      color: s.text, borderRadius: "20px",
      padding: "2px 10px", fontSize: "0.72rem",
      letterSpacing: "0.06em", textTransform: "uppercase",
    }}>
      {status}
    </span>
  );
}

export default function PriestDashboardPage({ priestId, secret }) {
  const [priest,   setPriest]   = useState(null);
  const [bookings, setBookings] = useState([]);
  const [tab,      setTab]      = useState("upcoming"); // upcoming | past | setup
  const [state,    setState]    = useState("loading");
  const [errMsg,   setErrMsg]   = useState("");
  const [joiningId, setJoiningId] = useState(null);
  const [roomData,  setRoomData]  = useState(null);

  useEffect(() => {
    if (!priestId || !secret) {
      setState("error");
      setErrMsg("Missing priest ID or secret in URL.");
      return;
    }
    Promise.all([
      fetch(`${API}/priest/${priestId}?secret=${encodeURIComponent(secret)}`).then(r => r.json()),
      fetch(`${API}/priest/${priestId}/bookings?secret=${encodeURIComponent(secret)}`).then(r => r.json()),
    ])
      .then(([p, b]) => {
        if (p.detail) throw new Error(p.detail);
        setPriest(p);
        setBookings(Array.isArray(b) ? b : []);
        setState("ready");
      })
      .catch(e => {
        setState("error");
        setErrMsg(e.message || "Could not load dashboard.");
      });
  }, [priestId, secret]);

  const today = new Date().toISOString().split("T")[0];

  const upcoming = bookings.filter(b =>
    b.status !== "cancelled" && b.status !== "completed" && b.preferred_date >= today
  );
  const past = bookings.filter(b =>
    b.status === "completed" || b.preferred_date < today
  );

  const totalEarnings = bookings
    .filter(b => b.status === "completed")
    .reduce((sum, b) => sum + (b.price || 0), 0);

  // Join puja room
  const handleJoin = async (booking) => {
    setJoiningId(booking.id);
    try {
      const res  = await fetch(`${API}/puja/priest-token/${booking.id}?secret=${encodeURIComponent(secret)}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to get token");
      setRoomData({
        config: {
          appId:      data.app_id,
          token:      data.token,
          channel:    data.channel,
          uid:        data.uid,
          priestUid:  data.uid,
          priestName: priest?.name || "Priest",
        },
        booking,
      });
    } catch (e) {
      alert("Could not join: " + (e.message || "Unknown error"));
    } finally {
      setJoiningId(null);
    }
  };

  // Full-screen room
  if (roomData) {
    return (
      <PujaRoom
        booking={{ puja_name: roomData.booking.puja_name, puja_emoji: "🔱" }}
        agoraConfig={roomData.config}
        onLeave={() => setRoomData(null)}
      />
    );
  }

  if (state === "loading") return <Centered><Spinner /></Centered>;

  if (state === "error") return (
    <Centered>
      <div style={{
        background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)",
        borderRadius: "12px", padding: "20px 28px",
        fontSize: "0.9rem", color: "#fca5a5", textAlign: "center", maxWidth: "400px",
      }}>
        {errMsg}
      </div>
    </Centered>
  );

  const shown = tab === "upcoming" ? upcoming : past;

  return (
    <div style={{ minHeight: "100vh", background: C.bg, padding: "32px 24px" }}>
      <div style={{ maxWidth: "720px", margin: "0 auto" }}>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: "20px", marginBottom: "32px" }}>
          <div style={{
            width: "60px", height: "60px",
            background: "linear-gradient(135deg, #E8832A, #C9953A)",
            borderRadius: "50%", display: "flex", alignItems: "center",
            justifyContent: "center", fontSize: "1.6rem", flexShrink: 0,
          }}>
            🔱
          </div>
          <div>
            <div style={{ fontFamily: "'Cinzel Decorative', serif", fontSize: "1.2rem", color: C.cream }}>
              {priest?.name}
            </div>
            <div style={{ fontSize: "0.82rem", color: C.dim, marginTop: "2px" }}>
              {(priest?.languages || []).join(" · ")} &nbsp;·&nbsp; {priest?.location || "—"}
            </div>
            <div style={{ marginTop: "6px" }}>
              <span style={{
                background: "rgba(52,211,153,0.12)", border: "1px solid rgba(52,211,153,0.3)",
                color: "#6ee7b7", borderRadius: "20px", padding: "2px 10px",
                fontSize: "0.72rem", letterSpacing: "0.06em",
              }}>
                APPROVED PRIEST
              </span>
            </div>
          </div>
        </div>

        {/* Stats row */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "14px", marginBottom: "28px" }}>
          {[
            ["Upcoming", upcoming.length, C.saffron],
            ["Completed", past.filter(b => b.status === "completed").length, "#6ee7b7"],
            ["Earnings", `₹${totalEarnings.toLocaleString("en-IN")}`, C.gold],
          ].map(([label, val, color]) => (
            <div key={label} style={{
              background: C.bg2, border: `1px solid ${C.border}`,
              borderRadius: "12px", padding: "16px 20px", textAlign: "center",
            }}>
              <div style={{ fontFamily: "'Cinzel Decorative', serif", fontSize: "1.5rem", color }}>{val}</div>
              <div style={{ fontSize: "0.78rem", color: C.dim, marginTop: "4px" }}>{label}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", gap: "0", marginBottom: "20px", borderBottom: `1px solid ${C.border}` }}>
          {[
            ["upcoming", `Upcoming (${upcoming.length})`],
            ["past",     `Past (${past.length})`],
            ["setup",    "Setup Guide"],
          ].map(([id, label]) => (
            <button key={id} onClick={() => setTab(id)} style={{
              background: "none", border: "none",
              borderBottom: tab === id ? `2px solid ${C.saffron}` : "2px solid transparent",
              color: tab === id ? C.saffron : C.dim,
              fontFamily: "'Crimson Pro', serif",
              fontSize: "1rem", padding: "10px 20px",
              cursor: "pointer", transition: "color 0.2s", marginBottom: "-1px",
            }}>
              {label}
            </button>
          ))}
        </div>

        {/* Setup Guide tab */}
        {tab === "setup" && <SetupGuide />}

        {/* Booking list */}
        {tab !== "setup" && (shown.length === 0 ? (
          <div style={{ textAlign: "center", color: C.dim, padding: "48px 0", fontStyle: "italic" }}>
            No {tab} bookings
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
            {shown.map(b => (
              <BookingCard
                key={b.id}
                booking={b}
                secret={secret}
                onJoin={() => handleJoin(b)}
                joining={joiningId === b.id}
              />
            ))}
          </div>
        ))}

        {/* Specializations */}
        {priest?.specializations?.length > 0 && (
          <div style={{
            background: C.bg2, border: `1px solid ${C.border}`,
            borderRadius: "14px", padding: "20px 24px", marginTop: "28px",
          }}>
            <div style={{
              fontFamily: "'Cinzel Decorative', serif",
              fontSize: "0.72rem", color: C.saffron,
              letterSpacing: "0.06em", marginBottom: "14px",
            }}>
              YOUR SPECIALIZATIONS
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
              {priest.specializations.map(s => (
                <span key={s} style={{
                  background: C.glow, border: `1px solid ${C.border}`,
                  borderRadius: "20px", padding: "4px 12px",
                  fontSize: "0.82rem", color: C.text,
                }}>
                  {s}
                </span>
              ))}
            </div>
          </div>
        )}

        <div style={{ textAlign: "center", marginTop: "32px", fontSize: "0.75rem", color: C.dim }}>
          VedSanga · Priest Portal · Contact team for support
        </div>
      </div>
    </div>
  );
}

function BookingCard({ booking: b, secret, onJoin, joining }) {
  const [expanded, setExpanded] = useState(false);
  const isToday   = b.preferred_date === new Date().toISOString().split("T")[0];
  const joinable  = b.status === "confirmed";

  return (
    <div style={{
      background: C.bg2,
      border: `1px solid ${isToday ? C.saffron : C.border}`,
      borderRadius: "14px", padding: "18px 22px",
      boxShadow: isToday ? "0 0 20px rgba(232,131,42,0.08)" : "none",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "6px" }}>
            <span style={{ fontFamily: "'Cinzel Decorative', serif", fontSize: "0.92rem", color: C.cream }}>
              {b.puja_name}
            </span>
            <StatusBadge status={b.status} />
            {isToday && (
              <span style={{
                background: "rgba(232,131,42,0.2)", border: `1px solid ${C.saffron}`,
                color: C.saffron, borderRadius: "20px",
                padding: "2px 8px", fontSize: "0.68rem", letterSpacing: "0.06em",
              }}>TODAY</span>
            )}
          </div>
          <div style={{ fontSize: "0.84rem", color: C.dim }}>
            {b.preferred_date} · {b.preferred_time} IST
            &nbsp;&nbsp;|&nbsp;&nbsp;
            {b.booking_type === "family" ? "Family" : "Individual"}
            &nbsp;&nbsp;|&nbsp;&nbsp;
            {b.tier ? b.tier.charAt(0).toUpperCase() + b.tier.slice(1) : "—"}
          </div>
          <div style={{ fontSize: "0.82rem", color: C.dim, marginTop: "4px" }}>
            Devotee: <span style={{ color: C.text }}>{b.name || "—"}</span>
            &nbsp;&nbsp;·&nbsp;&nbsp;
            ₹{(b.price || 0).toLocaleString("en-IN")}
          </div>
        </div>
        <div style={{ display: "flex", gap: "8px", marginLeft: "16px", flexShrink: 0 }}>
          {joinable && (
            <button
              onClick={onJoin}
              disabled={joining}
              style={{
                background: joining ? "rgba(232,131,42,0.3)" : "linear-gradient(135deg, #E8832A, #C9953A)",
                border: "none", borderRadius: "8px",
                padding: "8px 16px", color: "white",
                fontFamily: "'Crimson Pro', serif", fontSize: "0.88rem",
                cursor: joining ? "wait" : "pointer",
                whiteSpace: "nowrap",
              }}
            >
              {joining ? "Joining…" : "Join Puja"}
            </button>
          )}
          <button
            onClick={() => setExpanded(x => !x)}
            style={{
              background: "transparent", border: `1px solid ${C.border}`,
              borderRadius: "8px", padding: "8px 12px",
              color: C.dim, cursor: "pointer",
              fontSize: "0.85rem",
            }}
          >
            {expanded ? "▲" : "▼"}
          </button>
        </div>
      </div>

      {expanded && (
        <div style={{
          marginTop: "14px", paddingTop: "14px",
          borderTop: `1px solid rgba(201,149,58,0.1)`,
          fontSize: "0.82rem", color: C.dim,
          display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 24px",
        }}>
          {[
            ["Booking ID", b.id?.slice(0, 8) + "…"],
            ["Members",    b.members || "1"],
            b.gotra    && ["Gotra",    b.gotra],
            b.occasion && ["Occasion", b.occasion],
            b.sankalpa && ["Sankalpa", b.sankalpa],
          ].filter(Boolean).map(([k, v]) => (
            <div key={k}>
              <span style={{ color: C.dim }}>{k}: </span>
              <span style={{ color: C.text }}>{v}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const VIEWER_OPTIONS = [
  {
    id: "phone",
    label: "Second Phone",
    tag: "RECOMMENDED",
    tagColor: "#6ee7b7",
    tagBg: "rgba(52,211,153,0.12)",
    tagBorder: "rgba(52,211,153,0.3)",
    icon: "📱",
    pros: ["Mounts directly below Phone 1 on tripod — no extra table needed", "Gaze angle only 8–10° — invisible to devotees", "Easy to hold or reposition between puja phases"],
    cons: ["Small screen — harder to read faces in large Satsang sessions"],
    placement: "Mount 4–6 inches below Phone 1 on the same tripod. Both face you at 2.5 feet.",
    diagram: [
      ["        ┌──────────────┐", C => C.dim],
      ["        │  📱 Phone 1  │  ← altar camera (PRIEST role)", C => C.text],
      ["        └──────────────┘", C => C.dim],
      ["               │   4–6 inches", C => C.dim],
      ["        ┌──────────────┐", C => C.dim],
      ["        │  📱 Phone 2  │  ← devotee viewer (muted)", C => C.text],
      ["        └──────────────┘", C => C.dim],
      ["               │  tripod arm", C => C.dim],
      ["    ◄── 2.5 feet ──► YOU", C => "#6ee7b7"],
    ],
  },
  {
    id: "tablet",
    label: "Tablet (iPad / Android)",
    tag: "BEST SCREEN",
    tagColor: C.gold,
    tagBg: "rgba(201,149,58,0.1)",
    tagBorder: "rgba(201,149,58,0.3)",
    icon: "📟",
    pros: ["Largest screen — all devotee faces clearly visible even in Satsang (10 families)", "Can be flat on altar table — natural downward glance, feels devotional not technical", "Easier to read names and reactions at a distance"],
    cons: ["Needs a tablet stand or propped position", "Cannot be on the tripod with Phone 1 (too bulky)"],
    placement: "Place flat on altar table or on a small stand, positioned between you and the tripod, screen facing you.",
    diagram: [
      ["        ┌──────────────┐", C => C.dim],
      ["        │  📱 Phone 1  │  ← altar camera on tripod", C => C.text],
      ["        └──────────────┘", C => C.dim],
      ["               │  2.5 feet", C => C.dim],
      ["              YOU", C => "#6ee7b7"],
      ["               │  altar table", C => C.dim],
      ["      ┌──────────────────┐", C => C.dim],
      ["      │  📟  Tablet       │  ← flat on table, screen up", C => C.text],
      ["      └──────────────────┘", C => C.dim],
    ],
  },
  {
    id: "laptop",
    label: "Laptop",
    tag: "ACCEPTABLE",
    tagColor: "#fde68a",
    tagBg: "rgba(250,204,21,0.08)",
    tagBorder: "rgba(250,204,21,0.3)",
    icon: "💻",
    pros: ["Large screen — devotees are easily visible", "Already available in most homes, no extra purchase"],
    cons: ["Screen is usually lower than eye level — obvious downward gaze if not raised", "Laptop camera publishes an extra video stream — disable it immediately on joining"],
    placement: "Place on a table or floor directly in front of you. Use a laptop stand or stack of books to raise the screen closer to eye level. Disable the laptop camera before joining.",
    diagram: [
      ["        ┌──────────────┐", C => C.dim],
      ["        │  📱 Phone 1  │  ← altar camera on tripod", C => C.text],
      ["        └──────────────┘", C => C.dim],
      ["               │  2.5 feet", C => C.dim],
      ["              YOU", C => "#6ee7b7"],
      ["               │  table / floor", C => C.dim],
      ["      ┌────────────────────┐", C => C.dim],
      ["      │  💻 Laptop (raised) │  ← camera OFF, mic OFF", C => C.text],
      ["      └────────────────────┘", C => C.dim],
    ],
  },
];

function SetupGuide() {
  const [activeViewer, setActiveViewer] = useState("phone");
  const viewer = VIEWER_OPTIONS.find(o => o.id === activeViewer);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "18px" }}>

      {/* Intro */}
      <div style={{
        background: "rgba(232,131,42,0.07)", border: `1px solid rgba(232,131,42,0.3)`,
        borderRadius: "14px", padding: "18px 22px",
      }}>
        <div style={{ fontFamily: "'Cinzel Decorative', serif", fontSize: "0.78rem", color: C.saffron, letterSpacing: "0.06em", marginBottom: "10px" }}>
          TWO DEVICES — WHY YOU NEED BOTH
        </div>
        <div style={{ fontSize: "0.88rem", color: C.text, lineHeight: "1.8" }}>
          You always need <strong style={{ color: C.cream }}>one phone on a tripod</strong> pointing at the altar.
          That is Device A — it streams the ritual to devotees.<br />
          Device B is for <strong style={{ color: C.cream }}>you to see devotees' faces</strong> — so they feel seen,
          not just watched. Device B can be a second phone, a tablet, or a laptop.
        </div>
        <div style={{
          marginTop: "12px", padding: "10px 14px",
          background: "rgba(239,68,68,0.07)", border: "1px solid rgba(239,68,68,0.25)",
          borderRadius: "8px", fontSize: "0.82rem", color: "#fca5a5",
        }}>
          ✗ &nbsp;<strong>Laptop-only does not work.</strong> A laptop camera faces your face, not your altar.
          Devotees would see only you and never the deity, the diya, or the ritual — which defeats the purpose.
          You always need a phone as the altar camera.
        </div>
      </div>

      {/* Viewer device selector */}
      <div style={{
        background: C.bg2, border: `1px solid ${C.border}`,
        borderRadius: "14px", padding: "20px 24px",
      }}>
        <div style={{ fontFamily: "'Cinzel Decorative', serif", fontSize: "0.78rem", color: C.saffron, letterSpacing: "0.06em", marginBottom: "16px" }}>
          CHOOSE YOUR VIEWER DEVICE (Device B)
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", gap: "10px", marginBottom: "20px", flexWrap: "wrap" }}>
          {VIEWER_OPTIONS.map(o => (
            <button key={o.id} onClick={() => setActiveViewer(o.id)} style={{
              background: activeViewer === o.id ? "rgba(201,149,58,0.15)" : "transparent",
              border: `1px solid ${activeViewer === o.id ? C.gold : C.border}`,
              borderRadius: "10px", padding: "8px 16px",
              color: activeViewer === o.id ? C.cream : C.dim,
              fontFamily: "'Crimson Pro', serif", fontSize: "0.9rem",
              cursor: "pointer", display: "flex", alignItems: "center", gap: "6px",
            }}>
              {o.icon} {o.label}
              {activeViewer === o.id && (
                <span style={{
                  marginLeft: "4px", background: o.tagBg, border: `1px solid ${o.tagBorder}`,
                  color: o.tagColor, borderRadius: "10px", padding: "1px 7px",
                  fontSize: "0.65rem", letterSpacing: "0.05em",
                }}>
                  {o.tag}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Diagram */}
        <div style={{
          fontFamily: "monospace", fontSize: "0.8rem", color: C.text,
          lineHeight: "1.8", background: "rgba(0,0,0,0.3)",
          borderRadius: "10px", padding: "16px 20px", marginBottom: "16px",
        }}>
          {viewer.diagram.map(([line, colorFn], i) => (
            <div key={i} style={{ color: colorFn(C) }}>{line}</div>
          ))}
        </div>

        {/* Placement note */}
        <div style={{
          fontSize: "0.84rem", color: C.text, lineHeight: "1.7",
          padding: "12px 16px", background: "rgba(201,149,58,0.05)",
          borderRadius: "8px", marginBottom: "16px",
        }}>
          <strong style={{ color: C.gold }}>Position: </strong>{viewer.placement}
        </div>

        {/* Pros / Cons */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px" }}>
          <div>
            <div style={{ fontSize: "0.74rem", color: "#6ee7b7", letterSpacing: "0.05em", marginBottom: "8px" }}>ADVANTAGES</div>
            {viewer.pros.map(p => (
              <div key={p} style={{ display: "flex", gap: "8px", fontSize: "0.82rem", color: C.text, marginBottom: "6px" }}>
                <span style={{ color: "#6ee7b7", flexShrink: 0 }}>✓</span>{p}
              </div>
            ))}
          </div>
          <div>
            <div style={{ fontSize: "0.74rem", color: "#fca5a5", letterSpacing: "0.05em", marginBottom: "8px" }}>LIMITATIONS</div>
            {viewer.cons.map(c => (
              <div key={c} style={{ display: "flex", gap: "8px", fontSize: "0.82rem", color: C.dim, marginBottom: "6px" }}>
                <span style={{ color: "#fca5a5", flexShrink: 0 }}>–</span>{c}
              </div>
            ))}
          </div>
        </div>

        {/* Laptop-specific extra warning */}
        {activeViewer === "laptop" && (
          <div style={{
            marginTop: "14px", padding: "12px 16px",
            background: "rgba(250,204,21,0.07)", border: "1px solid rgba(250,204,21,0.25)",
            borderRadius: "8px", fontSize: "0.82rem", color: "#fde68a",
          }}>
            Before joining the session on laptop: open the browser, go to camera settings, and <strong>turn off the camera</strong>.
            If the laptop publishes video it counts as an extra stream and increases Agora cost. Laptop mic must also be muted.
          </div>
        )}
      </div>

      {/* Distance & eye contact */}
      <div style={{
        background: C.bg2, border: `1px solid ${C.border}`,
        borderRadius: "14px", padding: "20px 24px",
      }}>
        <div style={{ fontFamily: "'Cinzel Decorative', serif", fontSize: "0.78rem", color: C.saffron, letterSpacing: "0.06em", marginBottom: "14px" }}>
          DISTANCE &amp; EYE CONTACT
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 24px", fontSize: "0.84rem", marginBottom: "14px" }}>
          {[
            ["Phone 1 (tripod) distance", "2.5 feet from your face"],
            ["Phone 1 height", "Eye level — pupils align with lens"],
            ["Gaze angle to viewer device", "8–15° downward — reads as calm attention"],
            ["Tablet / laptop raised", "Closer to eye level = smaller gaze shift"],
          ].map(([k, v]) => (
            <div key={k}>
              <div style={{ color: C.dim, fontSize: "0.76rem", marginBottom: "2px" }}>{k}</div>
              <div style={{ color: C.cream }}>{v}</div>
            </div>
          ))}
        </div>
        <div style={{
          padding: "12px 16px",
          background: "rgba(52,211,153,0.07)", border: "1px solid rgba(52,211,153,0.2)",
          borderRadius: "8px", fontSize: "0.82rem", color: "#6ee7b7",
        }}>
          Any downward glance of less than 15° looks like thoughtful presence to devotees — not distraction.
          Raise your viewer device as high as practical to keep this angle small.
        </div>
      </div>

      {/* Session phase guide */}
      <div style={{
        background: C.bg2, border: `1px solid ${C.border}`,
        borderRadius: "14px", padding: "20px 24px",
      }}>
        <div style={{ fontFamily: "'Cinzel Decorative', serif", fontSize: "0.78rem", color: C.saffron, letterSpacing: "0.06em", marginBottom: "14px" }}>
          WHEN TO LOOK AT WHICH DEVICE
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          {[
            ["🙏 Sankalpa & welcome",   "Viewer device — make eye contact, say devotees' names"],
            ["🔱 Main puja ritual",      "Phone 1 (altar) — devotees watch the ritual"],
            ["🪔 Aarti",                 "Phone 1 — hold diya close to camera for devotees to see flame"],
            ["📿 Mantras & chanting",    "Phone 1 or eyes closed — devotees chant along"],
            ["🤲 Ashirvad / blessing",   "Viewer device — look at each devotee as you bless them"],
            ["💬 Q&A / close",          "Viewer device — full attention on devotees' faces"],
          ].map(([phase, guidance]) => (
            <div key={phase} style={{
              display: "flex", gap: "14px", alignItems: "flex-start",
              padding: "10px 14px", borderRadius: "8px",
              background: "rgba(201,149,58,0.04)",
            }}>
              <div style={{ fontSize: "0.88rem", color: C.cream, minWidth: "190px" }}>{phase}</div>
              <div style={{ fontSize: "0.82rem", color: C.dim, flex: 1 }}>{guidance}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Equipment checklist */}
      <div style={{
        background: C.bg2, border: `1px solid ${C.border}`,
        borderRadius: "14px", padding: "20px 24px",
      }}>
        <div style={{ fontFamily: "'Cinzel Decorative', serif", fontSize: "0.78rem", color: C.saffron, letterSpacing: "0.06em", marginBottom: "14px" }}>
          EQUIPMENT CHECKLIST
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "8px", fontSize: "0.85rem" }}>
          {[
            ["✅", "Phone 1 on tripod — joins as PRIEST (publishes altar camera + audio)", C.text],
            ["✅", "Viewer device (phone / tablet / laptop) — joins as DEVOTEE, camera OFF, mic OFF", C.text],
            ["✅", "Dual phone tripod mount if using second phone (Amazon India ₹500–₹1,500)", C.text],
            ["✅", "Tablet stand or laptop stand to raise viewer screen near eye level", C.text],
            ["✅", "Both devices fully charged or plugged in before session", C.text],
            ["✅", "Good Wi-Fi — test before session; keep router close to altar space", C.text],
            ["✅", "Front-facing lamp or ring light to illuminate your face for Phone 1", C.text],
            ["⚠️", "Do NOT use earphones on Phone 1 — your chanting must carry clearly", "#fde68a"],
            ["⚠️", "Viewer device camera and mic MUST be off — no extra audio or video streams", "#fde68a"],
          ].map(([icon, text, color]) => (
            <div key={text} style={{ display: "flex", gap: "10px", color, lineHeight: "1.6" }}>
              <span style={{ flexShrink: 0 }}>{icon}</span>
              <span>{text}</span>
            </div>
          ))}
        </div>
      </div>

      <div style={{ textAlign: "center", fontSize: "0.8rem", color: C.dim, padding: "12px 0 4px" }}>
        Questions about setup? Contact the VedSanga team for support.
      </div>
    </div>
  );
}

function Centered({ children }) {
  return (
    <div style={{ minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
      {children}
    </div>
  );
}

function Spinner() {
  return (
    <div style={{ textAlign: "center" }}>
      <div style={{ fontSize: "3rem", color: C.saffron, animation: "pulse-om 2s ease-in-out infinite" }}>ॐ</div>
      <div style={{ color: C.dim, fontSize: "0.88rem", marginTop: "12px", fontStyle: "italic" }}>Loading…</div>
    </div>
  );
}
